import {
  lazy,
  Suspense,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ArrowLeft, Check, Code2, Eye, FileText, Send } from 'lucide-react';

import { ResizableSplitPane } from '@/shared/ui/primitives';
import { Button } from '@/shared/ui/shadcn/button';
import { Tabs, TabsList, TabsTrigger } from '@/shared/ui/shadcn/tabs';

import { DocumentAttachmentList, type DocumentEditorAttachment } from './DocumentAttachmentList';
import { DocumentEditorPage } from './DocumentEditorPage';

import styles from './DocumentEditorWorkspace.module.css';

const DocumentPdfPreview = lazy(() =>
  import('./DocumentPdfPreview').then((module) => ({ default: module.DocumentPdfPreview }))
);

export interface DocumentEditorWorkspaceProps {
  backLabel: string;
  onBack(): void;
  title: string;
  subtitle: string;
  saved?: boolean;
  onSend(): void;
  sendDisabled?: boolean;
  markdown: string;
  onMarkdownChange(markdown: string): void;
  placeholder?: string;
  /** Lexical plugins for the visual editor, e.g. fax number formatting. */
  editorPlugins?: ReactNode;
  /** Normalizes Markdown typed in the source view. */
  formatMarkdown?(markdown: string): string;
  attachments: DocumentEditorAttachment[];
  onUploadAttachment(file: File): void | Promise<void>;
  onRemoveAttachment(attachmentId: string): void;
  uploadingAttachment?: boolean;
  attachmentError?: string | null;
  previewUrl: string | null;
  previewState: 'idle' | 'loading' | 'error';
  showPreview: boolean;
  onShowPreviewChange(showPreview: boolean): void;
  alert?: string | null;
  /** Rendered above the editor toolbar, e.g. recipient fields. */
  fields?: ReactNode;
  /** Rendered above the preview. */
  previewToolbar?: ReactNode;
  /** Replaces the PDF preview when set. */
  previewContent?: ReactNode;
  children?: ReactNode;
}

/** Split-pane document editor: PDF preview on the left, Markdown editor on the right. */
export function DocumentEditorWorkspace({
  backLabel,
  onBack,
  title,
  subtitle,
  saved = false,
  onSend,
  sendDisabled = false,
  markdown,
  onMarkdownChange,
  placeholder,
  editorPlugins,
  formatMarkdown,
  attachments,
  onUploadAttachment,
  onRemoveAttachment,
  uploadingAttachment = false,
  attachmentError,
  previewUrl,
  previewState,
  showPreview,
  onShowPreviewChange,
  alert,
  fields,
  previewToolbar,
  previewContent,
  children,
}: DocumentEditorWorkspaceProps) {
  const [activeMode, setActiveMode] = useState<'visual' | 'markdown'>('visual');
  const editorPanelRef = useRef<HTMLElement | null>(null);
  const previewSpacerRef = useRef<HTMLDivElement | null>(null);
  const [previewTopOffset, setPreviewTopOffset] = useState(0);
  const [previewPageCount, setPreviewPageCount] = useState(0);
  const handlePreviewPageCountChange = useCallback((pageCount: number) => {
    setPreviewPageCount(pageCount);
  }, []);

  useLayoutEffect(() => {
    const editorPanel = editorPanelRef.current;
    if (editorPanel === null) {
      return;
    }

    let frame: number | null = null;
    const updatePreviewAlignment = () => {
      if (frame !== null) {
        cancelAnimationFrame(frame);
      }
      frame = requestAnimationFrame(() => {
        frame = null;
        const editorPage = editorPanel.querySelector<HTMLElement>(`.${styles.documentPage}`);
        const spacer = previewSpacerRef.current;
        if (editorPage === null || spacer === null) {
          return;
        }
        // Measure from the spacer so content above the preview (a toolbar)
        // and above the editor page (fields) are both accounted for.
        const nextTopOffset = Math.max(
          0,
          editorPanel.getBoundingClientRect().top +
            editorPage.offsetTop -
            spacer.getBoundingClientRect().top
        );
        setPreviewTopOffset((currentTopOffset) =>
          nextTopOffset === 0 && currentTopOffset > 0 ? currentTopOffset : nextTopOffset
        );
      });
    };

    const resizeObserver = new ResizeObserver(updatePreviewAlignment);
    resizeObserver.observe(editorPanel);
    updatePreviewAlignment();
    window.addEventListener('resize', updatePreviewAlignment);
    return () => {
      if (frame !== null) {
        cancelAnimationFrame(frame);
      }
      resizeObserver.disconnect();
      window.removeEventListener('resize', updatePreviewAlignment);
    };
  }, [activeMode, markdown, alert, showPreview]);

  const previewPanel = (
    <section className={styles.previewPanel} aria-label="Document preview panel">
      {previewToolbar}
      <div
        ref={previewSpacerRef}
        className={styles.previewAlignmentSpacer}
        style={{ height: previewPageCount > 1 || previewContent ? 0 : previewTopOffset }}
        aria-hidden="true"
      />
      <div className={styles.previewContent}>
        {previewContent ?? (
          <Suspense
            fallback={
              <div className="flex h-full min-h-0 items-center justify-center bg-muted/30 p-4">
                <div className="flex min-h-[42rem] w-full max-w-3xl items-center justify-center rounded-md border bg-white text-sm text-muted-foreground">
                  Loading PDF preview…
                </div>
              </div>
            }
          >
            <DocumentPdfPreview
              previewUrl={previewUrl}
              previewState={previewState}
              onPageCountChange={handlePreviewPageCountChange}
            />
          </Suspense>
        )}
      </div>
    </section>
  );
  const editorPanel = (
    <section
      ref={editorPanelRef}
      className="relative flex h-full min-h-0 flex-col bg-background"
      aria-label="Document editor"
    >
      {fields}
      <div className="flex shrink-0 items-center gap-3 border-b px-4 py-3">
        <Tabs
          value={activeMode}
          onValueChange={(value) => setActiveMode(value as 'visual' | 'markdown')}
          className="shrink-0 gap-0"
        >
          <TabsList>
            <TabsTrigger value="visual">
              <Eye className="mr-1.5 size-3.5" />
              Visual
            </TabsTrigger>
            <TabsTrigger value="markdown" aria-label="Markdown">
              <Code2 className="size-3.5" />
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <Button
          type="button"
          variant={showPreview ? 'secondary' : 'outline'}
          className="ml-auto px-3"
          onClick={() => onShowPreviewChange(!showPreview)}
          aria-pressed={showPreview}
        >
          <FileText className="mr-1.5 size-3.5" />
          {showPreview ? 'Hide preview' : 'Preview'}
        </Button>
      </div>
      {alert ? (
        <p className="border-b px-4 py-2 text-xs text-destructive" role="alert">
          {alert}
        </p>
      ) : null}
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <DocumentEditorPage
          mode={activeMode}
          documentMarkdown={markdown}
          onChange={onMarkdownChange}
          placeholder={placeholder}
          plugins={editorPlugins}
          formatMarkdown={formatMarkdown}
          onUploadAttachment={onUploadAttachment}
          uploadState={uploadingAttachment ? 'uploading' : 'idle'}
        />
      </div>
      {attachmentError ? (
        <p className="border-t px-3 py-2 text-xs text-destructive" role="alert">
          {attachmentError}
        </p>
      ) : null}
      <DocumentAttachmentList attachments={attachments} onRemove={onRemoveAttachment} />
    </section>
  );

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <header className="flex shrink-0 items-center gap-3 border-b px-4 py-3">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onBack}
          aria-label={`Back to ${backLabel.toLowerCase()}`}
        >
          <ArrowLeft className="size-4" />
          {backLabel}
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-sm font-semibold">{title}</h1>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
        {saved ? <Check className="size-4 text-emerald-600" aria-label="Saved" /> : null}
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={onSend}
          disabled={sendDisabled}
        >
          <Send className="mr-1.5 size-3.5" />
          Send
        </Button>
      </header>

      {showPreview ? (
        <ResizableSplitPane
          className="min-h-0 flex-1"
          defaultFirstSize={48}
          minFirstSize={30}
          maxFirstSize={70}
          minSecondSize={30}
          maxSecondSize={70}
          withHandle
          first={previewPanel}
          second={editorPanel}
        />
      ) : (
        <div className="min-h-0 flex-1">{editorPanel}</div>
      )}

      {children}
    </div>
  );
}
